import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setCurrentShipTo from "@insite/client-framework/Store/Context/Handlers/SetCurrentShipTo";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import {
    getCurrentShipToState,
    getShipTosDataView,
} from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    BillToModel,
    CountryModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";

import CustomerAddressForm from "@insite/content-library/Components/CustomerAddressForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import deleteShipToAddress from "@tSurya/Handlers/DeleteShipToHandler";
import refreshShipTos from "@tSurya/Handlers/RefreshShipTos";
import updateShipToAddress from "@tSurya/Handlers/UpdateShipToAddress";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@tSurya/Overrides/Components/AddressInfoDisplay";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design.surya";
import { MyAccountThemeWrapper } from "@tSurya/Utils/MyAccountThemeWrapper";
import * as React from "react";
import { createContext, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    shipTosDataView: getShipTosDataView(state, state.pages.addresses.getShipTosParameter),
    getShipTosParameter: state.pages.addresses.getShipTosParameter,
    currentBillTo: getCurrentBillToState(state).value,
    currentShipTo: getCurrentShipToState(state).value,
    countries: getCurrentCountries(state),
    addressFieldDataView: getAddressFieldsDataView(state),
});

const mapDispatchToProps = {
    setCurrentShipTo,
    updateShipToAddress,
    deleteShipToAddress,
    refreshShipTos,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface AddressBookStyles {
    gridContainer?: GridContainerProps;
    titleTextGridItem?: GridItemProps;
    addressHeaderGridItem?: GridItemProps;
    titleText?: TypographyProps;
    noAddressesText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    addressCardGridItem?: GridItemProps;
    addressFormModal?: ModalPresentationProps;
    addressCard?: AddressCardStyles;
}

interface AddressCardStyles {
    gridContainer?: GridContainerProps;
    addressInfoDisplayGridItem?: GridItemProps;
    addressInfoDisplay?: AddressInfoDisplayStyles;
    actionsGridItem?: GridItemProps;
    actionOverflowIcon?: IconPresentationProps;
    actionUseAsShippingAddressButton?: ButtonPresentationProps;
    actionDeleteButton?: ButtonPresentationProps;
    bottomBorderGridItem?: GridItemProps;
    bottomBorder?: InjectableCss;
    overflowMenu?: OverflowMenuPresentationProps;
}

export const addressBookStyles: AddressBookStyles = {
    gridContainer: { gap: 20 },
    titleTextGridItem: { width: 12 },
    addressHeaderGridItem: { width: 12 },
    titleText: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.heading};
            font-size: 20px;
            font-weight: 700;
            line-height: 22px;
            font-style: normal;
            height: 22px;
        `,
    },
    noAddressesText: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.heading};
            font-size: 20px;
            font-weight: 700;
            line-height: 22px;
            font-style: normal;
            height: 22px;
            display: block;
            margin: auto;
            padding: 0 20px;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    addressCardGridItem: { width: 12 },
    addressCard: {
        gridContainer: { gap: 20 },
        addressInfoDisplayGridItem: { width: [10, 10, 5, 7, 7] },
        actionsGridItem: {
            width: [2, 2, 7, 5, 5],
            css: css`
                justify-content: flex-end;
            `,
        },
        actionUseAsShippingAddressButton: {
            variant: "secondary",
        },
        actionDeleteButton: {
            variant: "secondary",
        },

        bottomBorderGridItem: { width: 12 },
        bottomBorder: {
            css: css`
                height: 1px;
                background-color: ${COLORS.border.main};
                width: 100%;
            `,
        },
        overflowMenu: {
            cssOverrides: {
                menu: css`
                    width: 210px;
                `,
            },
        },
    },
    addressFormModal: { sizeVariant: "medium" },
};

const styles = addressBookStyles;

type ContextType = {
    isSettingShipTo: boolean;
    setIsSettingShipTo: (value: boolean) => void;
};

export const AddressBookContext = createContext<ContextType>({
    isSettingShipTo: false,
    setIsSettingShipTo: value => {},
});

const AddressBook: React.FC<Props> = ({
    id,
    shipTosDataView,
    toaster,
    setCurrentShipTo,
    updateShipToAddress,
    addressFieldDataView,
    countries,
    currentBillTo,
    currentShipTo,
    getShipTosParameter,
    deleteShipToAddress,
    refreshShipTos,
}) => {
    const [isSettingShipTo, setIsSettingShipTo] = useState(false);
    const shipTosForCurrentCustomer =
        currentBillTo && shipTosDataView.value
            ? shipTosDataView.value.filter(
                  shipTo =>
                      shipTo.customerNumber.trim().toLowerCase() ===
                      currentBillTo.customerNumber.trim().toLowerCase(),
              )
            : undefined;

    React.useEffect(() => {
        if (!shipTosDataView.value && !shipTosDataView.isLoading) {
            refreshShipTos({});
        }
    }, [shipTosDataView.value, shipTosDataView.isLoading]);

    let component;
    if (shipTosDataView.isLoading || !shipTosForCurrentCustomer || !addressFieldDataView.value) {
        component = (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    } else if (shipTosForCurrentCustomer.length > 0) {
        component = shipTosForCurrentCustomer.map(shipTo => (
            <GridItem {...styles.addressCardGridItem} key={shipTo.id.toString()}>
                <AddressCard
                    shipTo={shipTo}
                    setCurrentShipTo={setCurrentShipTo}
                    updateShipToAddress={updateShipToAddress}
                    deleteShipToAddress={deleteShipToAddress}
                    refreshShipTos={refreshShipTos}
                    toaster={toaster}
                    billToAddressFields={addressFieldDataView.value!.billToAddressFields}
                    shipToAddressFields={addressFieldDataView.value!.shipToAddressFields}
                    countries={countries}
                    currentBillTo={currentBillTo}
                    currentShipTo={currentShipTo}
                />
            </GridItem>
        ));
    } else {
        component = (
            <StyledWrapper {...styles.centeringWrapper}>
                <Typography {...styles.noAddressesText}>
                    {getShipTosParameter.filter
                        ? siteMessage("Addresses_NoResultsFound")
                        : siteMessage("Addresses_NoAddressesFound")}
                </Typography>
            </StyledWrapper>
        );
    }

    return (
        <MyAccountThemeWrapper>
            <AddressBookContext.Provider value={{ isSettingShipTo, setIsSettingShipTo }}>
                <GridContainer {...styles.gridContainer}>
                    <GridItem {...styles.titleTextGridItem}>
                        <Typography as="h2" {...styles.titleText}>
                            {translate("Address Book")}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.addressHeaderGridItem}>
                        <Zone contentId={id} zoneName="Content00" />
                    </GridItem>
                    {component}
                </GridContainer>
            </AddressBookContext.Provider>
        </MyAccountThemeWrapper>
    );
};

interface AddressCardProps {
    shipTo: ShipToModel;
    currentBillTo?: BillToModel;
    currentShipTo?: ShipToModel;
    countries?: CountryModel[];
    billToAddressFields?: AddressFieldDisplayCollectionModel;
    shipToAddressFields?: AddressFieldDisplayCollectionModel;
    setCurrentShipTo: Props["setCurrentShipTo"];
    updateShipToAddress: Props["updateShipToAddress"];
    deleteShipToAddress: Props["deleteShipToAddress"];
    refreshShipTos: Props["refreshShipTos"];
    toaster: Props["toaster"];
}

const AddressCard: React.FunctionComponent<AddressCardProps> = (props: AddressCardProps) => {
    const { shipTo } = props;
    const componentStyles: AddressCardStyles = styles.addressCard || {};

    return (
        <GridContainer {...componentStyles.gridContainer}>
            <GridItem
                {...componentStyles.addressInfoDisplayGridItem}
                data-test-selector="addressInfoDisplay_addressBook"
            >
                <AddressInfoDisplay
                    firstName={shipTo.firstName}
                    lastName={shipTo.lastName}
                    companyName={shipTo.companyName}
                    attention={shipTo.attention}
                    address1={shipTo.address1}
                    address2={shipTo.address2}
                    address3={shipTo.address3}
                    address4={shipTo.address4}
                    city={shipTo.city}
                    state={shipTo.state ? shipTo.state.abbreviation : undefined}
                    county={shipTo.properties.County || shipTo.properties.county}
                    postalCode={shipTo.postalCode}
                    country={shipTo.country ? shipTo.country.abbreviation : undefined}
                    phone={shipTo.phone}
                    fax={shipTo.fax}
                    email={shipTo.email}
                    extendedStyles={componentStyles.addressInfoDisplay}
                    addressType={shipTo.properties.residentialFlag}
                    dockdoor={shipTo.properties.dockDoorRequired}
                />
            </GridItem>
            <GridItem {...componentStyles.actionsGridItem}>
                <AddressActions
                    shipTo={shipTo}
                    updateShipToAddress={props.updateShipToAddress}
                    setCurrentShipTo={props.setCurrentShipTo}
                    deleteShipToAddress={props.deleteShipToAddress}
                    refreshShipTos={props.refreshShipTos}
                    toaster={props.toaster}
                    currentBillTo={props.currentBillTo}
                    currentShipTo={props.currentShipTo}
                    countries={props.countries}
                    shipToAddressFields={props.shipToAddressFields}
                    billToAddressFields={props.billToAddressFields}
                />
            </GridItem>
            <GridItem {...componentStyles.bottomBorderGridItem}>
                <StyledWrapper {...componentStyles.bottomBorder} />
            </GridItem>
        </GridContainer>
    );
};

interface AddressActionsProps {
    shipTo: ShipToModel;
    currentBillTo?: BillToModel;
    currentShipTo?: ShipToModel;
    countries?: CountryModel[];
    billToAddressFields?: AddressFieldDisplayCollectionModel;
    shipToAddressFields?: AddressFieldDisplayCollectionModel;
    setCurrentShipTo: Props["setCurrentShipTo"];
    updateShipToAddress: Props["updateShipToAddress"];
    deleteShipToAddress: Props["deleteShipToAddress"];
    refreshShipTos: Props["refreshShipTos"];
    toaster: Props["toaster"];
}

const AddressActions: React.FunctionComponent<AddressActionsProps> = (props: AddressActionsProps) => {
    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    const { shipTo, currentShipTo } = props;

    const addressBookContext = useContext(AddressBookContext);
    const actionsAreDisabled = addressBookContext.isSettingShipTo || shipTo.id === currentShipTo?.id;
    const disabledButtonStyles = actionsAreDisabled
        ? {
              color: COLORS.text.main,
              backgroundColor: COLORS.disabled.main,
              borderColor: COLORS.disabled.main,
          }
        : {};

    const useAsShippingAddressHandler = (e: any) => {
        e.preventDefault();
        addressBookContext.setIsSettingShipTo(true);
        props.setCurrentShipTo({
            shipToId: shipTo.id,
            onSuccess: () => {
                addressBookContext.setIsSettingShipTo(false);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const editClickHandler = () => {
        setModalIsOpen(true);
    };

    const formCancelHandler = (e: any) => {
        e.preventDefault();
        modalCloseHandler();
    };

    const deleteAddress = () => {
        props.deleteShipToAddress({
            shipToId: shipTo.id,
            onSuccess(result) {
                props.refreshShipTos({});
                props.toaster.addToast({
                    body: siteMessage(translate(result.message || "Address deleted successfully.")),
                    messageType: "success",
                });
            },
            onError(error) {
                props.toaster.addToast({
                    body: siteMessage(translate(error || "An error occurred while deleting the address.")),
                    messageType: "danger",
                });
            },
        });
    };

    const formSubmitHandler = (e: any, address: BaseAddressModel) => {
        e.preventDefault();
        const { currentBillTo, shipTo } = props;
        if (!currentBillTo || !shipTo) {
            return;
        }

        props.updateShipToAddress({
            billToId: currentBillTo.id,
            shipTo: {
                ...shipTo,
                ...address,
            },
            expand: ["validation"],
            onSuccess(updatedShipTo) {
                modalCloseHandler();
                props.refreshShipTos({});
                props.toaster.addToast({
                    body: translate(
                        "Address {0} was updated successfully.",
                        updatedShipTo.address1 || shipTo.address1,
                    ),
                    messageType: "success",
                });
            },
            onError(error) {
                props.toaster.addToast({
                    body: error || translate("An error occurred while updating the address."),
                    messageType: "danger",
                });
            },
        });
    };

    const { countries, shipToAddressFields } = props;
    const componentStyles: AddressCardStyles = styles.addressCard || {};

    return (
        <>
            <Hidden above="sm">
                <OverflowMenu position="end" {...componentStyles.overflowMenu}>
                    <Clickable
                        onClick={useAsShippingAddressHandler}
                        disabled={actionsAreDisabled}
                    >
                        {translate("Use as Shipping Address")}
                    </Clickable>
                    <Clickable onClick={editClickHandler}>{translate("Edit")}</Clickable>
                    {shipTo.id && (
                        <Clickable
                            disabled={actionsAreDisabled}
                            onClick={() => deleteAddress()}
                            data-test-selector="shipTo_Delete_Address"
                        >
                            {translate("Delete")}
                        </Clickable>
                    )}
                </OverflowMenu>
            </Hidden>
            <Hidden below="md">
                <Button
                    {...componentStyles.actionUseAsShippingAddressButton}
                    onClick={useAsShippingAddressHandler}
                    disabled={actionsAreDisabled}
                    data-test-selector="useAs_Shipping_Address"
                    style={{
                        whiteSpace: "nowrap",
                        minWidth: "fit-content",
                        paddingLeft: "8px",
                        paddingRight: "8px",
                        fontSize: "14px",
                        ...disabledButtonStyles,
                    }}
                >
                    {translate("Use as Shipping Address")}
                </Button>
                <StyledWrapper
                    css={css`
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-top: 10px;
                        width: 100%;
                    `}
                >
                    <Button
                        onClick={editClickHandler}
                        variant="secondary"
                        style={{ flexBasis: "45%", textAlign: "center" }}
                    >
                        {translate("Edit")}
                    </Button>
                    {shipTo.id && (
                        <Button
                            {...componentStyles.actionDeleteButton}
                            onClick={() => deleteAddress()}
                            disabled={actionsAreDisabled}
                            data-test-selector="shipTo_Delete_Address"
                            style={{ flexBasis: "45%", textAlign: "center", ...disabledButtonStyles }}
                        >
                            {translate("Delete")}
                        </Button>
                    )}
                </StyledWrapper>
            </Hidden>
            <Modal
                {...styles.addressFormModal}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
                headline={translate("Edit Shipping Information")}
            >
                {countries && shipToAddressFields && (
                    <CustomerAddressForm
                        address={shipTo}
                        countries={countries}
                        addressFieldDisplayCollection={shipToAddressFields}
                        onCancel={formCancelHandler}
                        onSubmit={formSubmitHandler}
                    />
                )}
            </Modal>
        </>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(AddressBook)),
    definition: {
        group: "Addresses",
        allowedContexts: ["AddressesPage"],
    },
};

export default widgetModule;
